<template>
  <div class="CompanySettings animated fadeIn">
    <MuiGridContainer
      :fluid="false"
      class="TemplateDefault__container"
    >
      <div class="row">
        <div class="col-12">
          <MuiCard
            :bg="false"
            :border="false"
            :padding-left="false"
            :padding-right="false"
            :padding-bottom="false"
            :padding-top="false"
            size="m"
            class="mx-auto"
          >
            <h1
              :class="[
                'pt-6',
                'pb-5',
              ]"
            >
              <span
                :class="[
                  'font-semibold',
                  'font-xl',
                ]"
              >
                {{ $t('Admin.companySettings.title') }}
              </span>
            </h1>

            <MuiForm
              ref="form"
              v-model="formData"
              @submit.native="cmFormSubmit"
            >
              <div
                class="pb-1"
              >
                <MuiInputText
                  v-model="ssCompanySettingsModule.formData.commercial_name"
                  :placeholder="$t('Admin.companySettings.displayCompanyName')"
                  :label="$t('Admin.companySettings.displayCompanySettings')"
                  required
                  full-width
                  name="commercial_name"
                  class="mb-0"
                />
                <span
                  :class="[
                    'pt-1',
                    'font-proxima',
                    'font-xs',
                    'color-ink-light',
                    'd-block',
                    'mb-3'
                  ]"
                >
                  {{ $t('Admin.companySettings.infoTextNameDisplayed') }}
                </span>
              </div>

              <div
                class="pb-1"
              >
                <MuiInputText
                  v-model="ssCompanySettingsModule.formData.fiscal_name"
                  :label="$t('Admin.companySettings.companyLegalName')"
                  :placeholder="$t('Admin.companySettings.companyLegalName')"
                  disabled
                  full-width
                  name="fiscal_name"
                />
              </div>

              <div
                class="pb-1"
              >
                <MuiInputText
                  v-model="ssCompanySettingsModule.formData.fiscal_number"
                  :label="$t('Admin.companySettings.taxNumber')"
                  :placeholder="$t('Admin.companySettings.taxNumber')"
                  disabled
                  full-width
                  name="fiscal_number"
                />
              </div>

              <div
                class="pb-1"
              >
                <MuiInputLocation
                  v-if="google"
                  :value="ssCompanySettingsModule.formData.address"
                  :required-address-components="[
                    'street_number',
                    'route',
                    'locality',
                    'administrative_area_level_1',
                    'administrative_area_level_2',
                    'country',
                    'postal_code',
                    'country_code',
                  ]"
                  :label="$t('Admin.companySettings.companyAddress')"
                  :google="google"
                  :placeholder="$t('Admin.companySettings.companyAddress')"
                  required
                  full-width
                  name="address"
                  @change="smSetFormAddress($event.address)"
                />
              </div>

              <div
                class="pb-1"
              >
                <MuiInputText
                  v-model="ssCompanySettingsModule.formData.address_details"
                  :label="$t('Admin.companySettings.floorDoorApartment')"
                  :placeholder="$t('Admin.companySettings.floorDoorApartment')"
                  full-width
                  name="address_details"
                />
              </div>

              <div
                class="pb-1"
              >
                <MuiInputPhone
                  v-model="ssCompanySettingsModule.formData.phone"
                  :optional-text="'(optional)'"
                  :label="$t('Admin.companySettings.phoneNumber')"
                  :placeholder="$t('Admin.companySettings.phoneNumber')"
                  required
                  full-width
                  default-country="GB"
                  name="phone"
                  country-label="Prefix"
                />
              </div>

              <div
                class="pb-1"
              >
                <MuiSelect
                  v-model="ssCompanySettingsModule.formData.size"
                  :return-object="false"
                  :label="$t('Admin.companySettings.companySize')"
                  full-width
                  required
                  name="size"
                  size="l"
                  object-label="label"
                  object-value="value"
                >
                  <MuiSelectItem
                    v-for="(item, i) in ssCompanySettingsModule.SIZE_OPTIONS"
                    :key="i"
                    :value="item"
                    :title="item.label"
                    type="main"
                  />
                </MuiSelect>
              </div>

              <div
                :class="[
                  'pt-2',
                  'pb-1',
                  'd-flex',
                  'justify-content-end',
                ]"
              >
                <MuiButtonFlat
                  :disabled="!formData.valid"
                  submit
                  class="TemplateDefault__MuiButton--fullMobile"
                >
                  {{ $t('Admin.companySettings.saveChangesButton') }}
                </MuiButtonFlat>
              </div>
            </MuiForm>

            <div
              v-if="ccShowCancel"
              :class="[
                'mt-4',
                'pb-4',
                'border-top-04',
                'border-color-ground-lightest'
              ]"
            />

            <div
              v-if="ccShowCancel"
              :class="[
                'pb-5',
                'd-flex',
                'justify-content-end',
              ]"
            >
              <MuiButtonFlat
                :type="'ground--offset'"
                @click.native="cmCancel"
              >
                {{ $t('Admin.companySettings.cancelCompanyAccount') }}
              </MuiButtonFlat>
            </div>
          </MuiCard>
        </div>
      </div>
    </MuiGridContainer>
  </div>
</template>

<script>

import {
  mapState,
  mapActions,
  mapMutations,
} from 'vuex';

import { gmapApi as google } from 'vue2-google-maps';
import {
  MuiInputText,
  MuiInputPhone,
  MuiSelect,
  MuiSelectItem,
  MuiInputLocation,
  MuiForm,
  MuiButtonFlat,
  MuiCard,
  MuiGridContainer,
} from '@ubeeqo/motion-ui';
import * as UserAccount from '@/store/modules/App/UserAccount/UserAccountModule';

import CompanySettingsUpdateModal from './components/CompanySettingsUpdateModalComponent';

import * as CompanySettingsModule from './CompanySettingsModule';

const modalIdUpdate = 'CompanySettingsUpdateModal';

export default {
  name: 'CompanySettingsView',
  components: {
    MuiGridContainer,
    MuiInputText,
    MuiInputPhone,
    MuiSelect,
    MuiSelectItem,
    MuiInputLocation,
    MuiForm,
    MuiButtonFlat,
    MuiCard,
  },
  data() {
    return {
      formData: {},
    };
  },
  computed: {
    ...mapState(UserAccount.FULL_NAMESPACE, {
      ssUserAccount: state => state.data,
    }),
    ...mapState(CompanySettingsModule.FULL_NAMESPACE, {
      ssCompanySettingsModule: s => s,
    }),
    google: () => google(),
    getComponentCompanySettingsUpdateModal() {
      return {
        component: CompanySettingsUpdateModal,
      };
    },
    ccShowCancel() {
      // TODO: Cancel Button - https://ubeeqo.atlassian.net/browse/PH-10064
      return false;
    },
  },
  mounted() {
    this.saEnterView();
  },
  destroyed() {
    this.saLeaveView();
  },
  methods: {
    ...mapActions(CompanySettingsModule.FULL_NAMESPACE, {
      saEnterView: 'enterView',
      saCompanyUpdate: 'companyUpdate',
      saLeaveView: 'leaveView',
    }),
    ...mapMutations(CompanySettingsModule.FULL_NAMESPACE, {
      smSetFormAddress: 'SET_FORM_ADDRESS',
    }),
    cmCancel() {
      // TODO: Cancel Button - https://ubeeqo.atlassian.net/browse/PH-10064
      // console.log('Cancel Button');
    },
    cmFormSubmit() {
      if (this.formData.valid) {
        this.cmShowModalUpdate();
        this.saCompanyUpdate()
          .then(() => {
            this.$modal.close(modalIdUpdate);
          });
      }
    },
    cmShowModalUpdate() {
      this.$modal.show(modalIdUpdate, {
        content: this.getComponentCompanySettingsUpdateModal,
        title: 'modalTitle',
        headerBgColor: 'secondary',
        newHeader: true,
        headerCrossBgColor: 'secondary',
        closeButton: false,
        cancelButton: null,
        acceptButton: null,
        size: 'small',
      });
    },
  },
};
</script>
